import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import Decimal from 'decimal.js';

import { OrderDetails } from '../common/contracts';
import { PAGINATION_ROWS_PER_PAGE } from '../constants/constants';
import {
  EQueryKeys,
  SortingDirection,
  SortingOrderValue
} from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { EToasterStatus, showToast } from '../utils/showToast';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

export interface OrdersQueryResponse {
  orders: OrderDetails[];
  currentPage: number;
  rowsPerPage: number;
  totalCount: number;
}

export default function useOrders(
  query?: string | number,
  pagination = { currentPage: 0, rowsPerPage: PAGINATION_ROWS_PER_PAGE },
  sorting = {
    sortValue: SortingOrderValue.DATE,
    direction: SortingDirection.DESC
  },
  filters?: {
    playerId?: string;
    publisherPurchaseId?: string;
    receiptId?: string;
    orderId?: string;
    statuses?: string[];
    isFree?: boolean;
    startDate?: string;
    endDate?: string;
    selectedProjects?: string;
  }
) {
  const axios = useAxios();
  const offset = pagination.currentPage * pagination.rowsPerPage;
  const recordLimit = pagination.rowsPerPage;
  const { sortValue, direction } = sorting;

  const queryOptions: UseQueryOptions<OrdersQueryResponse> = {
    queryKey: [
      EQueryKeys.ORDERS,
      offset,
      recordLimit,
      sortValue,
      direction,
      filters,
      pagination.currentPage // Explicitly add page to ensure unique keys
    ],
    queryFn: async () => {
      if (filters?.selectedProjects?.length === 0) {
        return {
          orders: [],
          currentPage: pagination.currentPage,
          rowsPerPage: pagination.rowsPerPage,
          totalCount: 0
        };
      }
      try {
        const response = await axios.get(
          getApiRouteOrMock(EApiRoutes.ORDERS),
          {
            offset,
            recordLimit,
            sortValue,
            direction,
            ...filters
          },
          filters?.selectedProjects
            ? { 'x-publishers': filters?.selectedProjects }
            : undefined
        );

        // BUG: amountTotal is not int32 as expected, so round it
        const orders = response.results.map((order: any) => ({
          ...order,
          amountTotal: new Decimal(Math.round(order.amountTotal)),
          amountFormatted: `${order.currencySymbol}${new Decimal(
            order.amountTotal
          )
            .div(100)
            .toNumber()}`
        }));

        const totalCount = response.totalCount;

        return {
          orders: [...orders],
          currentPage: pagination.currentPage,
          rowsPerPage: pagination.rowsPerPage,
          totalCount
        };
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message ||
          error?.response?.data ||
          error?.message ||
          'An error occurred while fetching orders';

        showToast({
          message: 'Failed to load orders',
          description: errorMessage,
          status: EToasterStatus.ERROR
        });

        throw error; // Re-throw to let React Query handle it
      }
    },
    enabled: !!filters?.startDate && !!filters?.endDate,
    staleTime: 0,
    cacheTime: 0, // Don't cache at all
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false, // Don't retry on error to avoid multiple error toasts
  };

  const getOrders = useQuery(queryOptions);

  const getOrderDetails = useQuery({
    ...fetchConfig.single,
    enabled: !!query,
    retry: false,
    queryKey: [EQueryKeys.ORDER_DETAILS, query],
    queryFn: async () => {
      const response = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.ORDER_DETAILS)}/${query}`,
        undefined,
        filters?.selectedProjects
          ? { 'x-publishers': filters?.selectedProjects }
          : undefined
      );

      const order: OrderDetails = {
        ...response,
        history: response.history.reverse(),
        amountInDollar: new Decimal(response.amountInDollar),
        amountNetInDollar: new Decimal(response.amountNetInDollar),
        amountNetTotal: new Decimal(response.amountNetTotal),
        taxRatePoints: new Decimal(response.taxRatePoints),
        taxTotal: new Decimal(response.taxTotal),
        taxAmountInDollar: new Decimal(response.taxAmountInDollar),
        discountAmount: response.discountAmount
          ? new Decimal(response.discountAmount)
          : 0
      };
      return order;
    }
  });

  const makeRefund = useMutation(
    async ({
      orderId,
      publisherId
    }: {
      orderId: string;
      publisherId?: string;
    }) => {
      let customHeader = publisherId
        ? { 'x-publishers': publisherId }
        : filters?.selectedProjects
          ? { 'x-publishers': filters?.selectedProjects }
          : undefined;
      return await axios.post(
        `${getApiRouteOrMock(EApiRoutes.REFUND)}/${orderId}`,
        {},
        customHeader
      );
    },
    {}
  );

  const retryAward = useMutation(
    async ({
      orderId,
      publisherId
    }: {
      orderId: string;
      publisherId?: string;
    }) => {
      let customHeader = publisherId
        ? { 'x-publishers': publisherId }
        : undefined;
      return await axios.post(
        `${getApiRouteOrMock(EApiRoutes.ORDERS)}/${orderId}/retry-award`,
        {},
        customHeader
      );
    },
    {}
  );

  return {
    getOrders,
    getOrderDetails,
    makeRefund,
    retryAward
  };
}
